/**
 * Quiz-project panels (PlayerScene › FirstPanel / CanvasQuizzPoints,
 * QuizzesPlayerController.cs, QuizzRanquing.cs):
 *
 *  - NamePanel: white screen asking for a nickname (input + play button).
 *  - RankingPanel: white screen with the ranking rows (#, name, points; mine in
 *    black, others grey, alternate stripes) and a "Next" button. With
 *    `hideScore` it shows a blank panel for 1.5 s and moves on by itself.
 */
import { useEffect, useState, type FormEvent } from "react";
import { libraryUrl } from "../data/media";
import type { RankingEntry } from "../data/answers";
import { t } from "./i18n";

const ui = (n: string) => libraryUrl(`ui/${n}.png`);

export function NamePanel({ onStart, onCancel }: { onStart: (name: string) => void; onCancel?: () => void }) {
  const [name, setName] = useState("");
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const n = name.trim();
    if (n) onStart(n);
  };
  return (
    <form className="qp-panel qp-name" onSubmit={submit}>
      {onCancel && <button type="button" className="nav-btn qp-close" onClick={onCancel} aria-label={t("exit")} style={{ backgroundImage: `url(${ui("navigationClose")})` }} />}
      <input
        className="qp-input"
        style={{ backgroundImage: `url(${ui("input")})` }}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("nickname")}
        dir="auto"
        maxLength={40}
        autoFocus
        autoComplete="off"
        aria-label={t("nickname")}
      />
      <button type="submit" className="qp-play" disabled={!name.trim()} aria-label={t("start")} style={{ backgroundImage: `url(${ui("play")})` }} />
    </form>
  );
}

export function RankingPanel({ entries, hideScore, showRanking, onNext, onExit }: { entries: RankingEntry[] | null; hideScore: boolean; showRanking: boolean; onNext: () => void; onExit?: () => void }) {
  // QuizzRanquing: with the score hidden, a blank panel closes itself after 1.5 s.
  useEffect(() => {
    if (!hideScore) return;
    const id = window.setTimeout(onNext, 1500);
    return () => window.clearTimeout(id);
  }, [hideScore, onNext]);
  if (hideScore) return <div className="qp-panel" />;

  const rows = entries ? (showRanking ? entries : entries.filter((e) => e.mine)) : null;
  return (
    <div className="qp-panel qp-ranking">
      {onExit && <button className="nav-btn qp-close" onClick={onExit} aria-label={t("exit")} style={{ backgroundImage: `url(${ui("navigationClose")})` }} />}
      <div className="qp-rows">
        {rows === null && <div className="qp-loading">…</div>}
        {rows?.map((e, i) => (
          <div key={e.id ?? `me${i}`} className={`qp-row ${i % 2 === 0 ? "odd" : ""} ${e.mine ? "mine" : ""}`}>
            <span className="qp-num">{i + 1}</span>
            <span className="qp-user" dir="auto">{e.userName}</span>
            <span className="qp-points">{e.totalPoints}</span>
          </div>
        ))}
      </div>
      <NextButton onClick={onNext} className="qp-next" />
    </div>
  );
}

/** bgButton + "Next" + arrow (GoToRanking / CanvasQuizzPoints › NextButton). */
export function NextButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button className={`go-on ${className ?? ""}`} onClick={onClick} aria-label={t("next")} style={{ backgroundImage: `url(${ui("bgButton")})` }}>
      <span className="go-on-label">{t("next")}</span>
      <img src={ui("arrow")} alt="" draggable={false} />
    </button>
  );
}
